import React, { Component, Fragment } from 'react';
import { Grid } from '@material-ui/core';
import Loading from '../../Loading'
import FilterBar from '../../Common/FilterBar';
import meetingAPI from '../../../flowtrace-api/meetingAPI';
import ChartComponent from './chartComponent';

const chartTooltips = {
  invitedTime: "This shows how much meeting time was placed on each team's calendars. If three people from the same team are invited to a 30 minute meeting, that counts as 90 minutes of invited meeting time for that team. Use this to compare the meeting load being created for each team, including invites that may still be pending or later declined.",
  acceptedTime: "This shows the meeting time that people in each team accepted. It counts the scheduled length for every accepted team member, so a 30 minute meeting accepted by three people from the same team counts as 90 minutes. Compare this with invited meeting time to see how much of the calendar load is actually being committed to.",
  meetingsInvolved: "This counts the meetings where at least one person from the team was invited. A meeting is counted once for a team even if several people from that team were invited. It is useful for seeing how broadly each team is pulled into meetings, separate from how many people attended each one.",
  meetingsOrganized: "This counts meetings organized by people in each team. It shows which teams are creating or owning meetings, not just attending them. A higher number can mean the team is coordinating more work, driving more cross-team activity, or carrying more of the meeting setup responsibility.",
  organizedRatio: "This compares the meetings a team organized with the meetings that involved that team. A higher percentage means the team is creating more of the meetings it appears in. A lower percentage means the team is mostly being invited into meetings organized elsewhere.",
  lateRatio: "This shows the share of recorded video call joins where people from the team joined after the scheduled start time. It only uses meetings where Flowtrace has video join data, and ignores unusual joins more than 15 minutes early or more than 60 minutes late. Use it as a punctuality signal for online meetings, not as a count of every calendar invite.",
  lateAverage: "This shows the average delay when someone from the team joined a recorded video call late. On-time and early joins are not included in this average, so it answers: when this team is late, how late are they typically?",
  lateTotal: "This adds up all late join time for people in each team across recorded video calls. If two people join the same meeting 10 minutes late, that contributes 20 minutes. It helps show the total amount of meeting time lost to late starts or late joins.",
  videoCallTime: "This shows the total time people in each team spent in recorded video calls that Flowtrace could match to calendar meetings. It is based on actual call attendance duration rather than scheduled calendar length, so it can differ from invited or accepted meeting time."
};

class MeetingOverview extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, searchWarning:null, queryFilters: { range_type: "calendar", range_period:"week", range_limit: 4} };
  }


  async loadMeetingStatistics(queryFilters) {
    this.setState({ searchWarning:null});


    const [{histogram, total}] = await Promise.all([
      meetingAPI.teams(queryFilters, this.props.flowtraceUser),
    ]);


    const dataSet = {
      series:[],
      categories:[],
    };

    // Let's add the series to the dataset with team name and total
    total.forEach(t => {
      const series = {name: t.team, total: t, data:[]};
      dataSet.series.push(series);
    })
    // Let's add the histogram data to the dataset series with categories
    histogram.forEach(h => {
      dataSet.categories.push(h.key_as_string);
      h.by_team.forEach(t => {
        const found = dataSet.series.find(s => s.name === t.team);
        if(found) {
          found.data.push(t);
        }
        else 
          console.log("Could not find team in series?!?", t.team, dataSet.series);
      });
    });
    console.log(dataSet);
    return this.setState({loading: false, ...dataSet});
  }

  onChange(queryFilters) {
    this.setState({ loading: true, queryFilters });
    this.loadMeetingStatistics(queryFilters);
  }

  componentDidMount() {
    this.loadMeetingStatistics(this.state.queryFilters);
  }

  componentWillUnmount() { this.setState = () => { return; }; }

  render() {
    if(this.state.loading)
      return (
        <Fragment>
          <FilterBar teamFilter meetingFilter searchWarning={this.state.searchWarning} loading={this.state.loading} flowtraceUser={ this.props.flowtraceUser } queryFilters={{...this.state.queryFilters }} onChange={this.onChange.bind(this)} />
          <Loading text={"This feature requires Google or Outlook integration. Please inquire your account manager for more details."}/>
        </Fragment>);

    return (
      <Fragment>
        <FilterBar teamFilter meetingFilter searchWarning={this.state.searchWarning} loading={this.state.loading} flowtraceUser={ this.props.flowtraceUser } queryFilters={{...this.state.queryFilters }} onChange={this.onChange.bind(this)} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Invited Meeting Time" seriesKey="time.invited" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.invitedTime}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Accepted Meeting Time" seriesKey="time.accepted" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.acceptedTime}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Meetings Involved" seriesKey="meetings" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.meetingsInvolved}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Meetings Organized" seriesKey="organized" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.meetingsOrganized}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Organized vs. Other Meetings" seriesKey="organized_vs_meetings_ratio" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.organizedRatio}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Late vs. On-time Meetings" seriesKey="video.late_ratio" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.lateRatio}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Late on Average" seriesKey="video.late_avg" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.lateAverage}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Total Time Late" seriesKey="video.late_sum" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.lateTotal}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartComponent title="Total time in video calls" seriesKey="video.duration_sum" series={this.state.series} categories={this.state.categories} tooltip={chartTooltips.videoCallTime}/>
              </Grid>
            </Grid>
      </Fragment>
    );
  } // end of render()
} // end of class

export default MeetingOverview
